import React, { useContext, useMemo } from 'react'
import { Paper, Box, Typography, Chip, Stack } from '@mui/material'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { TenantSettingsContext } from '../../App'

const VaultItemCard = ({ item }) => {
  const navigate = useNavigate()
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const allTeams = useMemo(
    () => [
      ...(orgSettings?.teams ?? []),
      ...(orgSettings?.additionalTeams ?? []),
    ],
    [orgSettings],
  )
  const allRoles = useMemo(() => [...(orgSettings?.roles ?? [])], [orgSettings])

  const normalise = (arr = []) =>
    [...new Set(arr.map((value) => String(value).trim().toLowerCase()))].sort()

  const arraysMatch = (a = [], b = []) => {
    const aNorm = normalise(a)
    const bNorm = normalise(b)

    if (aNorm.length !== bNorm.length) return false
    return aNorm.every((value, index) => value === bNorm[index])
  }

  const showAllRoles =
    item?.roles?.length > 0 && arraysMatch(item.roles, allRoles)
  const showAllTeams =
    item?.teams?.length > 0 && arraysMatch(item.teams, allTeams)

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        // Unread = shared with you and not opened yet (server-computed)
        ...(item?.unread && {
          borderLeft: 4,
          borderColor: 'primary.main',
        }),
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: 6,
          cursor: 'pointer',
        },
      }}
      onClick={() => navigate(item._id)}
    >
      <Stack height="100%" justifyContent="space-between">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="title2"
            gutterBottom
            sx={{ fontWeight: item?.unread ? 700 : undefined }}
          >
            {item.title}
          </Typography>

          {item?.unread && (
            <Chip
              label="Unread"
              size="small"
              color="primary"
              sx={{ borderRadius: 1, ml: 1, flexShrink: 0 }}
            />
          )}

          <Typography variant="body2" color="text.secondary">
            {item.assignedTo}
          </Typography>
        </Box>

        <Stack spacing={2} mt={1} mb={1} flexWrap="wrap">
          {item?.tags?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {item.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{ borderRadius: 1 }}
                  color="anPurple"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          {item?.roles?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {showAllRoles ? (
                <Chip
                  label="All Roles"
                  size="small"
                  sx={{ borderRadius: 1 }}
                  color="primary"
                  variant="outlined"
                />
              ) : (
                item.roles.map((role, index) => (
                  <Chip
                    key={index}
                    label={role}
                    size="small"
                    sx={{ borderRadius: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                ))
              )}
            </Stack>
          )}

          {item?.teams?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {showAllTeams ? (
                <Chip
                  label="All Teams"
                  size="small"
                  sx={{ borderRadius: 1 }}
                  color="contrast"
                  variant="outlined"
                />
              ) : (
                item.teams.map((team, index) => (
                  <Chip
                    key={index}
                    label={team}
                    size="small"
                    sx={{ borderRadius: 1 }}
                    color="contrast"
                    variant="outlined"
                  />
                ))
              )}
            </Stack>
          )}

          {item?.archived && (
            <Chip
              label="Archived"
              size="small"
              sx={{ borderRadius: 1 }}
              color="error"
              variant="outlined"
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {moment(item.createdAt).format('D MMM YY')}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default VaultItemCard
